import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import {
  catchError,
  filter,
  map,
  merge,
  shareReplay,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { BaseDialog } from '../base-dialog';
import { BASE_DIALOG_IMPORTS } from '../dialog-imports';
import { SelectDialogData, SelectDialogOption } from '../dialog.model';

@Component({
  selector: 'app-select-dialog',
  imports: [BASE_DIALOG_IMPORTS, MatRadioButton, MatRadioGroup, ReactiveFormsModule],
  templateUrl: './select-dialog.component.html',
  styleUrl: './select-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectDialogComponent
  extends BaseDialog<SelectDialogData<string>>
  implements OnInit
{
  private readonly _dialogRef =
    inject<MatDialogRef<SelectDialogComponent>>(MatDialogRef);
  protected readonly control = new FormControl('', { nonNullable: true });
  protected readonly loading = signal(false);
  protected readonly submitted = signal(false);

  constructor() {
    super();
  }

  ngOnInit(): void {
    const selected = this.data.options.find(
      (options: SelectDialogOption<string>) => options.checked
    )!;

    this.control.setValue(selected.value);
  }

  override handleConfirm(): void {
    if (
      this.data.onConfirm &&
      Object.prototype.toString.call(this.data.onConfirm) == '[object Function]'
    ) {
      this.submitted.set(true);

      const delay = this.data.onConfirmLoadingDelay ?? 1000;

      const callback$ = this.data.onConfirm(this.control.value);
      const source$ = callback$.pipe(
        shareReplay(1),
        catchError((error) => {
          this.submitted.set(false);

          return throwError(() => error);
        })
      );

      const loading$ = merge(
        timer(delay).pipe(
          map(() => true),
          takeUntil(source$)
        ),

        source$.pipe(map(() => false))
      );

      loading$
        .pipe(
          tap((state) => this.loading.set(state)),
          filter((state) => !state),
          tap(() => this._dialogRef.close(true)),
          take(1)
        )
        .subscribe();

      return;
    }

    this._dialogRef.close(true);
  }
}
